import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Save, LayoutDashboard } from 'lucide-react';

const DEFAULTS = { showRecentPayments: false, showNasBandwidth: false, showRevenueTrend: false };

const WIDGETS = [
  { key: 'showRecentPayments', label: 'Recent Payments', desc: 'Table of the latest payments across all customers, below Today\'s Collection.' },
  { key: 'showNasBandwidth', label: 'NAS Bandwidth', desc: 'Per-poller live download/upload panel for each configured NAS device.' },
  { key: 'showRevenueTrend', label: 'Revenue Trend (6 Months)', desc: 'Line chart of monthly revenue, next to the New Customers chart.' },
];

const cardStyle = (on) => ({
  display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '14px 16px',
  background: on ? 'var(--cb-f0fdf4)' : 'var(--bg)', border: `1px solid ${on ? 'var(--cb-bbf7d0)' : 'var(--border)'}`,
  borderRadius: 8, marginBottom: 10,
});

// Toggles for optional Dashboard widgets that were removed from the default view
// (2026-08-02) to keep the page focused — this is the opt-back-in switch for each one.
export default function DashboardWidgetSettingsTab() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI.getDashboardWidgets()
      .then(r => setSettings({ ...DEFAULTS, ...(r.data.data || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => setSettings(s => ({ ...s, [key]: !s[key] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.saveDashboardWidgets(settings);
      toast.success('Dashboard widget settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
      <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading…
    </div>
  );

  return (
    <div className="card"><div className="card-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <LayoutDashboard size={16} />
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Dashboard Widgets</h3>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '.83rem', marginTop: 0, marginBottom: 20 }}>
        These widgets are hidden from the Dashboard by default. Turn any of them back on here — applies
        for everyone who views the Dashboard, not just this browser.
      </p>

      {WIDGETS.map(w => (
        <label key={w.key} style={cardStyle(settings[w.key])}>
          <input type="checkbox" checked={!!settings[w.key]} onChange={() => toggle(w.key)} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{w.label}</div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{w.desc}</div>
          </div>
        </label>
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} style={{ marginRight: 6 }} />
          {saving ? 'Saving…' : 'Save Dashboard Widget Settings'}
        </button>
      </div>
    </div></div>
  );
}
